'use client';

import { Box, Stack, Typography } from '@mui/material';
import Image from 'next/image';

import ListItem from '@/components/ListItem';
import { EngagementModel, EngagementModelColor } from '@/config/engagement-models.config';
import colors from '@/styles/themes/colors';
import { useGlowEffect } from '@/utils/hooks/useGlowEffect';

import styles from './EngagementModelCard.module.scss';

const colorMap: Record<EngagementModelColor, string> = {
  blue: colors.blue500,
  green: colors.green500,
};

interface EngagementModelCardProps {
  model: EngagementModel;
  title: string;
  description: string;
  features: string[];
}

const EngagementModelCard = ({ model, title, description, features }: EngagementModelCardProps) => {
  const accentColor = colorMap[model.color];
  const { ref, onMouseMove, onMouseLeave, style } = useGlowEffect({ color: accentColor });

  return (
    <Stack className={styles.card}>
      <Box ref={ref} onMouseMove={onMouseMove} onMouseLeave={onMouseLeave} style={style} className={styles.imageBox}>
        <Box component="span" className={styles.ellipseTopLeft} />
        <Box component="span" className={styles.ellipseBottomRight} />
        <Image
          src={model.image.src}
          alt={model.image.alt}
          fill
          className={styles.image}
          sizes="(min-width: 768px) 416px, 100vw"
        />
      </Box>
      <Stack spacing={2} mt={4}>
        <Typography variant="h4" component="h3" color={accentColor}>
          {title}
        </Typography>
        <Typography variant="body1" color={colors.graytone300} maxWidth={416}>
          {description}
        </Typography>
        <Stack component="ul" spacing={1.5} className={styles.featureList}>
          {features.map((feature, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <ListItem key={index} text={feature} variant={model.color} className={styles.featureItem} />
          ))}
        </Stack>
      </Stack>
    </Stack>
  );
};

export default EngagementModelCard;
